import { api } from './api';

/**
 * Error Alert Bindings.
 *
 * The authoritative link from an error code to a notification-engine master +
 * its alert rules (immediate / threshold / digest). Creating one is what makes
 * an error start raising notifications when it is logged. One binding is
 * authoritative per (errorCode, appId).
 */
const BASE = '/api/v1/error-alert-binding';

export type TriggerMode = 'IMMEDIATE' | 'THRESHOLD' | 'DIGEST';
export type DigestUnit = 'MINUTES' | 'HOURS' | 'DAYS';

/** Notify only after `count` occurrences within `windowMinutes`. */
export interface ThresholdRule {
  count?: number;
  windowMinutes?: number;
}

/** Send a batched summary every `every` `unit` (an interval, not a cron). */
export interface DigestRule {
  every?: number;
  unit?: DigestUnit;
  /** For a DAYS cadence: "HH:MM" send time. */
  anchorTime?: string | null;
}

export interface EscalationRule {
  enabled?: boolean;
  afterMinutes?: number;
}

/** When to fire, and how hard to throttle. */
export interface AlertRules {
  triggerMode: TriggerMode;
  minSeverity?: string | null;
  cooldownMinutes?: number;
  groupByField?: string | null;
  threshold?: ThresholdRule;
  digest?: DigestRule;
  escalation?: EscalationRule;
}

export interface ErrorAlertBinding {
  id: string;
  errorCode: string;
  notificationMasterId: string;
  appId?: string | null;
  rules: AlertRules;
  isActive: boolean;
  isDelete?: boolean;
  lastDigestSentAt?: string | null;
  createdAt?: string | null;
  updatedAt?: string | null;
}

export interface ErrorAlertBindingCreateRequest {
  errorCode: string;
  notificationMasterId: string;
  rules?: AlertRules;
  appId?: string | null;
}

export interface ErrorAlertBindingUpdateRequest {
  notificationMasterId?: string;
  rules?: AlertRules;
  isActive?: boolean;
}

export const errorAlertBindingService = {
  // Bindings for an error code (scoped to the caller's projects + global).
  list: async (errorCode?: string): Promise<ErrorAlertBinding[]> => {
    const response = await api.get<{ data: { data: ErrorAlertBinding[]; totalItems: number } }>(
      `${BASE}/list`,
      { params: { errorCode, limit: 50 } }
    );
    return response.data.data?.data ?? [];
  },

  // The single active binding for an error code, if any (one per errorCode+appId).
  getForError: async (errorCode: string): Promise<ErrorAlertBinding | null> => {
    const rows = await errorAlertBindingService.list(errorCode);
    return rows.find((b) => b.errorCode === errorCode) ?? rows[0] ?? null;
  },

  create: async (data: ErrorAlertBindingCreateRequest): Promise<ErrorAlertBinding> => {
    const response = await api.post<{ data: ErrorAlertBinding }>(`${BASE}/create`, data);
    return response.data.data;
  },

  update: async (id: string, data: ErrorAlertBindingUpdateRequest): Promise<ErrorAlertBinding> => {
    const response = await api.patch<{ data: ErrorAlertBinding }>(`${BASE}/update/${id}`, data);
    return response.data.data;
  },

  remove: async (id: string): Promise<void> => {
    await api.delete(`${BASE}/delete/${id}`);
  },
};
